// eyeq — трекер клиента и файлы фидбэка как данные индекса (ADR-0083).
//
// Клиентская PM-команда ведёт Excel-трекер (Delivery Log по локалям, Change
// Log, Meeting Log, QA Feedback Log, план) и присылает per-asset файлы
// флагов. Ручка принимает файл в виде СЫРЫХ листов (ячейки строками) — весь
// разбор заголовков живёт в воркере (`tracker_import.ts`), чтобы браузер и
// скрипт слали одно и то же и правило разбора было в одном месте.
//
// Ячейки: любые значения приводятся к строке; даты допустимы как ISO
// (YYYY-MM-DD[THH:MM:SS]), американские (M/D/YYYY) или Excel-serial —
// нормализует воркер.
import { z } from 'zod';

export const EyeqTrackerKindSchema = z.enum(['tracker', 'url_flags', 'final_qa']);
// final_qa — итоговый отчёт заказчика по батчу (лист с колонками Batch /
// Asset ID / Validation Status / metadata_result_reason / Comments): финальный
// вердикт после PM-проверки, идёт в rejection rate.
export type EyeqTrackerKind = z.infer<typeof EyeqTrackerKindSchema>;

export const EyeqTrackerSheetsSchema = z.record(z.string().max(80), z.array(z.array(z.string().max(4000)).max(64)).max(6000));

// ── POST /api/eyeq/delivery/trackerImport (machine) и
//    POST /api/eyeq/manager/trackerImport (manager) ─────────────────────────
export const EyeqTrackerImportReqSchema = z.object({
  actor: z.string().min(1).max(80),
  kind: EyeqTrackerKindSchema,
  file_name: z.string().max(200).default(''),
  sha256: z.string().regex(/^[0-9a-f]{64}$/),
  sheets: EyeqTrackerSheetsSchema,
});
export type EyeqTrackerImportReq = z.infer<typeof EyeqTrackerImportReqSchema>;

export const EyeqTrackerShipmentDiffSchema = z.object({
  shipment_id: z.string(),
  locale: z.string(),
  change: z.enum(['new', 'changed']),
  fields: z.array(z.string()),          // какие поля изменились (для 'changed')
});

export const EyeqTrackerImportResSchema = z.object({
  ok: z.literal(true),
  snapshot_id: z.number().int(),
  repeated: z.boolean(),                 // тот же sha256 уже загружен — ничего не менялось
  diff: z.object({
    shipments: z.array(EyeqTrackerShipmentDiffSchema),
    headers_updated: z.array(z.string()), // заголовки eyeq_shipments, у которых поменялись стадии
    changes_new: z.array(z.string()),     // новые CHG-id
    changes_updated: z.array(z.string()),
    meetings_new: z.array(z.string()),    // "YYYY-MM-DD · Client Weekly"
    feedback_new: z.number().int(),
    feedback_applied: z.number().int(),   // вердикты, легшие в историю отправок
    feedback_unresolved: z.number().int(),
    flags_new: z.number().int(),
    flags_by_flag: z.record(z.string(), z.number().int()),
    final_new: z.number().int().default(0),        // новых строк итогового отчёта
    final_applied: z.number().int().default(0),    // вердиктов, легших в историю отправок
    final_unresolved: z.number().int().default(0),
    final_by_status: z.record(z.string(), z.number().int()).default({}),
  }),
  unknown_shipments: z.array(z.string()), // имена батчей трекера, которых нет в реестре
  warnings: z.array(z.string()),
});
export type EyeqTrackerImportRes = z.infer<typeof EyeqTrackerImportResSchema>;

// ── GET /api/eyeq/manager/tracker — текущее состояние для «Обзора» ───────────
export const EyeqManagerTrackerResSchema = z.object({
  last_snapshot: z.object({
    id: z.number().int(), kind: z.string(), file_name: z.string(), uploaded_at: z.string(), actor: z.string(),
  }).nullable(),
  locales: z.array(z.object({
    shipment_id: z.string(), locale: z.string(), image_count: z.number().int(),
    pm_review: z.string(), status: z.string(), rejected_count: z.number().int(), rework_name: z.string(),
  })),
  changes: z.array(z.object({
    chg_id: z.string(), chg_date: z.string(), summary: z.string(), new_rule: z.string(), status: z.string(),
  })),
  meetings: z.array(z.object({
    met_date: z.string(), meeting: z.string(), notes: z.string(), actions: z.string(), status: z.string(),
  })),
  feedback_by_batch: z.array(z.object({ batch_id: z.string(), n: z.number().int(), applied: z.number().int() })),
  final_by_shipment: z.array(z.object({ shipment_id: z.string(), passed: z.number().int(), failed: z.number().int() })).default([]),
  flags_by_shipment: z.array(z.object({ shipment_id: z.string(), flag: z.string(), n: z.number().int() })),
});
export type EyeqManagerTrackerRes = z.infer<typeof EyeqManagerTrackerResSchema>;
